import { BankCode, BankConfiguration, BankTransaction, OFXTransaction } from '../../types/bank-reconciliation';

export interface BankSpecificMapping {
  normalizeDescription: (description: string, memo?: string) => string;
  categorizeTransaction: (description: string, amount: number) => string | null;
  extractAdditionalInfo: (transaction: OFXTransaction) => Partial<BankTransaction>;
  detectTransactionType: (description: string, amount: number) => 'debit' | 'credit';
}

export class BankMappingService {
  private static readonly BANK_MAPPINGS: Record<BankCode, BankSpecificMapping> = {
    '001': { // Banco do Brasil
      normalizeDescription: (description: string, memo?: string) => {
        let normalized = description || '';
        if (memo && memo !== description) {
          normalized += ` ${memo}`;
        }
        
        // Normalizações específicas do BB
        normalized = normalized
          .replace(/COMPRA CARTAO\s+(\d+)\s+/, 'CARTÃO ')
          .replace(/TED\s+(\d+)\s+/, 'TED ')
          .replace(/PIX\s+TRANSF\s+/, 'PIX ')
          .replace(/TARIFA\s+PACOTE\s+/, 'TARIFA CONTA ')
          .replace(/\s+/g, ' ')
          .trim();

        return normalized;
      },

      categorizeTransaction: (description: string, amount: number) => {
        const desc = description.toLowerCase();
        
        if (desc.includes('pix')) return 'transferencia';
        if (desc.includes('ted') || desc.includes('doc')) return 'transferencia';
        if (desc.includes('cartao') || desc.includes('cartão')) return 'cartao';
        if (desc.includes('tarifa')) return 'tarifa';
        if (desc.includes('saque')) return 'saque';
        if (desc.includes('deposito') || desc.includes('depósito')) return 'deposito';
        if (desc.includes('boleto')) return 'boleto';
        if (desc.includes('financiamento')) return 'financiamento';
        if (desc.includes('salario') || desc.includes('salário')) return 'salario';
        
        return null;
      },

      extractAdditionalInfo: (transaction: OFXTransaction) => {
        const description = transaction.name || transaction.memo || '';
        
        // Extrair número de referência para PIX
        const pixMatch = description.match(/PIX.*?(\d{8,})/);
        if (pixMatch) {
          return { reference_number: pixMatch[1] };
        }

        // Extrair número do cartão
        const cardMatch = description.match(/CARTAO.*?(\d{4})/);
        if (cardMatch) {
          return { document_number: `****${cardMatch[1]}` };
        }

        return {};
      },

      detectTransactionType: (description: string, amount: number) => {
        return amount >= 0 ? 'credit' : 'debit';
      }
    },

    '033': { // Santander
      normalizeDescription: (description: string, memo?: string) => {
        let normalized = description || '';
        if (memo && memo !== description) {
          normalized += ` ${memo}`;
        }

        normalized = normalized
          .replace(/COMPRA CARTAO\s+(\d+)\s+/, 'COMPRA CARTÃO ')
          .replace(/TRANSF ELETR\s+/, 'TRANSFERÊNCIA ')
          .replace(/PIX\s+ENVIADO\s+/, 'PIX ENVIADO ')
          .replace(/PIX\s+RECEBIDO\s+/, 'PIX RECEBIDO ')
          .replace(/\s+/g, ' ')
          .trim();

        return normalized;
      },

      categorizeTransaction: (description: string, amount: number) => {
        const desc = description.toLowerCase();
        
        if (desc.includes('pix')) return 'transferencia';
        if (desc.includes('transf')) return 'transferencia';
        if (desc.includes('compra cartao') || desc.includes('compra cartão')) return 'cartao';
        if (desc.includes('tarifa') || desc.includes('taxa')) return 'tarifa';
        if (desc.includes('saque')) return 'saque';
        if (desc.includes('aplicacao') || desc.includes('aplicação')) return 'investimento';
        if (desc.includes('resgate')) return 'investimento';
        
        return null;
      },

      extractAdditionalInfo: (transaction: OFXTransaction) => {
        const description = transaction.name || transaction.memo || '';
        
        // Santander específico - extrair CPF/CNPJ do PIX
        const cpfMatch = description.match(/CPF.*?(\d{3}\.\d{3}\.\d{3}-\d{2})/);
        if (cpfMatch) {
          return { document_number: cpfMatch[1] };
        }

        return {};
      },

      detectTransactionType: (description: string, amount: number) => {
        return amount >= 0 ? 'credit' : 'debit';
      }
    },

    '104': { // Caixa Econômica Federal
      normalizeDescription: (description: string, memo?: string) => {
        let normalized = description || '';
        if (memo && memo !== description) {
          normalized += ` ${memo}`;
        }

        normalized = normalized
          .replace(/PAGAMENTO\s+DE\s+/, 'PAGAMENTO ')
          .replace(/TRANSFERENCIA\s+/, 'TRANSFERÊNCIA ')
          .replace(/PIX\s+TRANSF\s+/, 'PIX ')
          .replace(/SAQUE\s+CARTAO\s+/, 'SAQUE CARTÃO ')
          .replace(/\s+/g, ' ')
          .trim();

        return normalized;
      },

      categorizeTransaction: (description: string, amount: number) => {
        const desc = description.toLowerCase();
        
        if (desc.includes('pix')) return 'transferencia';
        if (desc.includes('transferencia') || desc.includes('transferência')) return 'transferencia';
        if (desc.includes('pagamento')) return 'pagamento';
        if (desc.includes('saque')) return 'saque';
        if (desc.includes('fgts')) return 'governo';
        if (desc.includes('auxilio') || desc.includes('auxílio')) return 'governo';
        if (desc.includes('financiamento')) return 'financiamento';
        if (desc.includes('loteria')) return 'entretenimento';
        
        return null;
      },

      extractAdditionalInfo: (transaction: OFXTransaction) => {
        const description = transaction.name || transaction.memo || '';
        
        // FGTS/PIS específicos
        if (description.includes('FGTS') || description.includes('PIS')) {
          return { bank_category: 'governo' };
        }

        return {};
      },

      detectTransactionType: (description: string, amount: number) => {
        return amount >= 0 ? 'credit' : 'debit';
      }
    },

    '237': { // Bradesco
      normalizeDescription: (description: string, memo?: string) => {
        let normalized = description || '';
        if (memo && memo !== description) {
          normalized += ` ${memo}`;
        }

        normalized = normalized
          .replace(/COMPRA\s+CARTAO\s+/, 'COMPRA CARTÃO ')
          .replace(/DOC\/TED\s+/, 'TRANSFERÊNCIA ')
          .replace(/PIX\s+TRANSF\s+/, 'PIX ')
          .replace(/TARIFA\s+/, 'TARIFA ')
          .replace(/\s+/g, ' ')
          .trim();

        return normalized;
      },

      categorizeTransaction: (description: string, amount: number) => {
        const desc = description.toLowerCase();
        
        if (desc.includes('pix')) return 'transferencia';
        if (desc.includes('doc') || desc.includes('ted')) return 'transferencia';
        if (desc.includes('compra cartao') || desc.includes('compra cartão')) return 'cartao';
        if (desc.includes('tarifa')) return 'tarifa';
        if (desc.includes('investimento')) return 'investimento';
        if (desc.includes('capitalizacao') || desc.includes('capitalização')) return 'investimento';
        
        return null;
      },

      extractAdditionalInfo: (transaction: OFXTransaction) => {
        return {};
      },

      detectTransactionType: (description: string, amount: number) => {
        return amount >= 0 ? 'credit' : 'debit';
      }
    },

    '260': { // Nubank
      normalizeDescription: (description: string, memo?: string) => {
        let normalized = description || '';
        if (memo && memo !== description) {
          normalized += ` ${memo}`;
        }

        // Nubank tem descrições mais limpas, menos normalização necessária
        normalized = normalized
          .replace(/Pix\s+enviado\s+/, 'Pix enviado ')
          .replace(/Pix\s+recebido\s+/, 'Pix recebido ')
          .replace(/Transferência\s+enviada\s+/, 'Transferência enviada ')
          .replace(/Transferência\s+recebida\s+/, 'Transferência recebida ')
          .replace(/\s+/g, ' ')
          .trim();

        return normalized;
      },

      categorizeTransaction: (description: string, amount: number) => {
        const desc = description.toLowerCase();
        
        if (desc.includes('pix')) return 'transferencia';
        if (desc.includes('transferência') || desc.includes('transferencia')) return 'transferencia';
        if (desc.includes('pagamento')) return 'pagamento';
        if (desc.includes('ifood')) return 'alimentacao';
        if (desc.includes('uber') || desc.includes('99')) return 'transporte';
        if (desc.includes('netflix') || desc.includes('spotify')) return 'entretenimento';
        if (desc.includes('aplicação') || desc.includes('aplicacao')) return 'investimento';
        if (desc.includes('resgate')) return 'investimento';
        
        return null;
      },

      extractAdditionalInfo: (transaction: OFXTransaction) => {
        return {};
      },

      detectTransactionType: (description: string, amount: number) => {
        // Nubank uses positive/negative amounts
        return amount >= 0 ? 'credit' : 'debit';
      }
    },

    '341': { // Itaú
      normalizeDescription: (description: string, memo?: string) => {
        let normalized = description || '';
        if (memo && memo !== description) {
          normalized += ` ${memo}`;
        }

        normalized = normalized
          .replace(/COMPRA DEBITO\s+/, 'DÉBITO ')
          .replace(/COMPRA CREDITO\s+/, 'CRÉDITO ')
          .replace(/TRANSF ELETR\s+/, 'TRANSFERÊNCIA ')
          .replace(/PIX\s+TRANSF\s+/, 'PIX ')
          .replace(/APLIC AUT\s+/, 'APLICAÇÃO ')
          .replace(/\s+/g, ' ')
          .trim();

        return normalized;
      },

      categorizeTransaction: (description: string, amount: number) => {
        const desc = description.toLowerCase();
        
        if (desc.includes('pix')) return 'transferencia';
        if (desc.includes('transf eletr')) return 'transferencia';
        if (desc.includes('debito') || desc.includes('débito')) return 'cartao';
        if (desc.includes('credito') || desc.includes('crédito')) return 'cartao';
        if (desc.includes('aplicacao') || desc.includes('aplicação')) return 'investimento';
        if (desc.includes('resgate')) return 'investimento';
        if (desc.includes('tarifa')) return 'tarifa';
        if (desc.includes('iof')) return 'imposto';
        
        return null;
      },

      extractAdditionalInfo: (transaction: OFXTransaction) => {
        return {};
      },

      detectTransactionType: (description: string, amount: number) => {
        return amount >= 0 ? 'credit' : 'debit';
      }
    },

    '077': { // Inter
      normalizeDescription: (description: string, memo?: string) => {
        let normalized = description || '';
        if (memo && memo !== description) {
          normalized += ` ${memo}`;
        }

        normalized = normalized
          .replace(/Pix\s+enviado\s+/, 'Pix enviado ')
          .replace(/Pix\s+recebido\s+/, 'Pix recebido ')
          .replace(/TED\s+enviada\s+/, 'TED enviada ')
          .replace(/TED\s+recebida\s+/, 'TED recebida ')
          .replace(/\s+/g, ' ')
          .trim();

        return normalized;
      },

      categorizeTransaction: (description: string, amount: number) => {
        const desc = description.toLowerCase();
        
        if (desc.includes('pix')) return 'transferencia';
        if (desc.includes('ted')) return 'transferencia';
        if (desc.includes('débito') || desc.includes('debito')) return 'cartao';
        if (desc.includes('crédito') || desc.includes('credito')) return 'cartao';
        if (desc.includes('cdb') || desc.includes('lci') || desc.includes('lca')) return 'investimento';
        if (desc.includes('conta de luz') || desc.includes('conta de água')) return 'utilidades';
        
        return null;
      },

      extractAdditionalInfo: (transaction: OFXTransaction) => {
        return {};
      },

      detectTransactionType: (description: string, amount: number) => {
        return amount >= 0 ? 'credit' : 'debit';
      }
    },

    '208': { // BTG Pactual
      normalizeDescription: (description: string, memo?: string) => {
        let normalized = description || '';
        if (memo && memo !== description) {
          normalized += ` ${memo}`;
        }

        normalized = normalized
          .replace(/APLICACAO\s+/, 'APLICAÇÃO ')
          .replace(/RESGATE\s+/, 'RESGATE ')
          .replace(/PIX\s+/, 'PIX ')
          .replace(/TED\s+/, 'TED ')
          .replace(/\s+/g, ' ')
          .trim();

        return normalized;
      },

      categorizeTransaction: (description: string, amount: number) => {
        const desc = description.toLowerCase();
        
        if (desc.includes('aplicacao') || desc.includes('aplicação')) return 'investimento';
        if (desc.includes('resgate')) return 'investimento';
        if (desc.includes('fundo')) return 'investimento';
        if (desc.includes('acao') || desc.includes('ação')) return 'investimento';
        if (desc.includes('rf')) return 'investimento';
        if (desc.includes('pix')) return 'transferencia';
        if (desc.includes('ted')) return 'transferencia';
        if (desc.includes('corretagem')) return 'tarifa';
        if (desc.includes('taxa')) return 'tarifa';
        
        return null;
      },

      extractAdditionalInfo: (transaction: OFXTransaction) => {
        const description = transaction.name || transaction.memo || '';
        
        // Extrair código do fundo de investimento
        const fundMatch = description.match(/FUNDO\s+(\w+)/);
        if (fundMatch) {
          return { reference_number: fundMatch[1] };
        }

        return {};
      },

      detectTransactionType: (description: string, amount: number) => {
        return amount >= 0 ? 'credit' : 'debit';
      }
    },

    '756': { // Sicoob
      normalizeDescription: (description: string, memo?: string) => {
        let normalized = description || '';
        if (memo && memo !== description) {
          normalized += ` ${memo}`;
        }

        normalized = normalized
          .replace(/PIX\s+TRANSF\s+/, 'PIX ')
          .replace(/DOC\s+TRANSF\s+/, 'DOC ')
          .replace(/TED\s+TRANSF\s+/, 'TED ')
          .replace(/QUOTA\s+CAPITAL\s+/, 'QUOTA CAPITAL ')
          .replace(/\s+/g, ' ')
          .trim();

        return normalized;
      },

      categorizeTransaction: (description: string, amount: number) => {
        const desc = description.toLowerCase();
        
        if (desc.includes('pix')) return 'transferencia';
        if (desc.includes('doc') || desc.includes('ted')) return 'transferencia';
        if (desc.includes('quota')) return 'cooperativa';
        if (desc.includes('assembleia')) return 'cooperativa';
        if (desc.includes('custeio')) return 'agricultura';
        if (desc.includes('safra')) return 'agricultura';
        if (desc.includes('insumo')) return 'agricultura';
        
        return null;
      },

      extractAdditionalInfo: (transaction: OFXTransaction) => {
        return {};
      },

      detectTransactionType: (description: string, amount: number) => {
        return amount >= 0 ? 'credit' : 'debit';
      }
    },

    '748': { // Sicredi
      normalizeDescription: (description: string, memo?: string) => {
        let normalized = description || '';
        if (memo && memo !== description) {
          normalized += ` ${memo}`;
        }

        normalized = normalized
          .replace(/PIX\s+TRANSF\s+/, 'PIX ')
          .replace(/TRANSFERENCIA\s+/, 'TRANSFERÊNCIA ')
          .replace(/CAPITAL\s+SOCIAL\s+/, 'CAPITAL SOCIAL ')
          .replace(/FINANCIAMENTO\s+/, 'FINANCIAMENTO ')
          .replace(/\s+/g, ' ')
          .trim();

        return normalized;
      },

      categorizeTransaction: (description: string, amount: number) => {
        const desc = description.toLowerCase();
        
        if (desc.includes('pix')) return 'transferencia';
        if (desc.includes('transferencia') || desc.includes('transferência')) return 'transferencia';
        if (desc.includes('capital social')) return 'cooperativa';
        if (desc.includes('assembleia')) return 'cooperativa';
        if (desc.includes('financiamento')) return 'financiamento';
        if (desc.includes('agricultura')) return 'agricultura';
        if (desc.includes('quota')) return 'cooperativa';
        
        return null;
      },

      extractAdditionalInfo: (transaction: OFXTransaction) => {
        return {};
      },

      detectTransactionType: (description: string, amount: number) => {
        return amount >= 0 ? 'credit' : 'debit';
      }
    }
  };

  static getBankMapping(bankCode: BankCode): BankSpecificMapping {
    return this.BANK_MAPPINGS[bankCode];
  }

  static processTransaction(
    transaction: OFXTransaction, 
    bankCode: BankCode
  ): Partial<BankTransaction> {
    const mapping = this.getBankMapping(bankCode);
    if (!mapping) {
      return {
        description: transaction.name || transaction.memo || 'Transação sem descrição',
        original_description: `${transaction.name || ''} ${transaction.memo || ''}`.trim(),
        transaction_type: transaction.amount >= 0 ? 'credit' : 'debit'
      };
    }

    const normalizedDescription = mapping.normalizeDescription(
      transaction.name || '', 
      transaction.memo
    );

    const category = mapping.categorizeTransaction(normalizedDescription, transaction.amount);
    const additionalInfo = mapping.extractAdditionalInfo(transaction);
    const transactionType = mapping.detectTransactionType(normalizedDescription, transaction.amount);

    return {
      description: normalizedDescription,
      original_description: `${transaction.name || ''} ${transaction.memo || ''}`.trim(),
      bank_category: category || undefined,
      transaction_type: transactionType,
      ...additionalInfo
    };
  }

  static getAllSupportedBanks(): Array<{ code: BankCode; name: string }> {
    return Object.keys(this.BANK_MAPPINGS).map(code => ({
      code: code as BankCode,
      name: this.getBankName(code as BankCode)
    }));
  }

  private static getBankName(bankCode: BankCode): string {
    const bankNames: Record<BankCode, string> = {
      '001': 'Banco do Brasil',
      '033': 'Santander',
      '104': 'Caixa Econômica Federal',
      '237': 'Bradesco',
      '260': 'Nubank',
      '341': 'Itaú',
      '077': 'Inter',
      '208': 'BTG Pactual',
      '756': 'Sicoob',
      '748': 'Sicredi'
    };

    return bankNames[bankCode] || bankCode;
  }
}